import type { SeverityCount } from '@counsel-project/counsel-audit-ai-api'

type SeverityKey = '1' | '2' | '3'

export type AlertSeverityCounts = SeverityCount[]

export type NormalizedAlertCounts = {
  low: number
  medium: number
  critical: number
  total: number
}

type WithAlertsField = {
  alerts?: AlertSeverityCounts | Record<string, unknown>
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isAlertCountArray = (value: unknown): value is AlertSeverityCounts => Array.isArray(value)

const SEVERITY_KEY_ALIASES: Record<SeverityKey, string[]> = {
  '1': ['1', 'severity1', 'low'],
  '2': ['2', 'severity2', 'medium'],
  '3': ['3', 'severity3', 'high', 'critical'],
}

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const getSeverityCountFromRecord = (
  source: Record<string, unknown>,
  severity: SeverityKey
): number | undefined => {
  for (const key of SEVERITY_KEY_ALIASES[severity]) {
    const value = toNumberOrUndefined(source[key])
    if (value !== undefined) {
      return value
    }
  }

  return undefined
}

export const getSeverityCount = (source: unknown, severity: SeverityKey): number => {
  if (isAlertCountArray(source)) {
    const directMatch = source.find((entry) => String(entry?.severity) === severity)
    return toNumberOrUndefined(directMatch?.count) ?? 0
  }

  if (!isRecord(source)) return 0

  const directRecordCount = getSeverityCountFromRecord(source, severity)
  if (directRecordCount !== undefined) {
    return directRecordCount
  }

  const alerts = (source as WithAlertsField).alerts
  if (isAlertCountArray(alerts)) {
    const match = alerts.find((entry) => String(entry?.severity) === severity)
    return toNumberOrUndefined(match?.count) ?? 0
  }

  if (isRecord(alerts)) {
    return getSeverityCountFromRecord(alerts, severity) ?? 0
  }

  return 0
}

export const normalizeAlertSeverityCounts = (source: unknown): NormalizedAlertCounts => {
  const low = getSeverityCount(source, '1')
  const medium = getSeverityCount(source, '2')
  const critical = getSeverityCount(source, '3')

  return {
    low,
    medium,
    critical,
    total: low + medium + critical,
  }
}

export const getTotalSeverityCount = (source: unknown): number =>
  normalizeAlertSeverityCounts(source).total
